import { createContext, useEffect, useState } from 'react';
import { Icon } from '@components/icons';
import Button from '@components/common/button';
import { modals } from '@const/modals';
import styles from './styles.module.scss';
import classNames from 'classnames/bind';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useLocales } from '@hooks/helpers/useLocales';
import { motion } from 'framer-motion';
import { drawerAnimation, modalAnimation } from '@const/animation';

type TOpenModal<T = any> = {
    modalId: string;
    onSubmit?: (modalData: T) => void;
    onClose?: () => void;
};

export interface IModalComponentProps<T = any, R = any> {
    onSubmit?: (modalData?: T) => R;
    modalData: T;
    changeModalData: (value?: T) => void;
    handleChangeDisable: (value: boolean) => void;
    handleChangeLoading: (value: boolean) => void;
    isLoadingSubmit: boolean;
    modalState: TModal;
    changeModalState: (val: TModal) => void;
    onClose: () => void;
}

export type TModal = {
    confirmLabel?: string;
    cancelLabel?: string;
    isHeader?: boolean;
    ignoreOverlay?: boolean;
    submitVariant?: 'red' | 'primary';
    isFooter?: boolean;
    Component: React.FC<IModalComponentProps> | null;
    variant: 'medium' | 'small' | 'custom' | 'large' | 'drawer';
} & TOpenModal;

type TModalContext = {
    isOpen: boolean;
    onOpen: <T>(value: TOpenModal<T>) => void;
    rerender: number;
    repeatRequest: () => void;
    changeModalData: (value: any) => void;
    modalData: any;
    onClose: () => void;
    handleChangeDisable: (val: boolean) => void;
    handleChangeLoading: (val: boolean) => void;
    isLoadingSubmit: boolean;
    modalState: TModal;
    changeModalState: (val: Partial<TModal>) => void;
};

const initialState: TModal = {
    modalId: '',
    onSubmit: () => {},
    confirmLabel: '',
    cancelLabel: '',
    Component: null,
    variant: 'custom'
};

export const ModalContext = createContext<TModalContext>({
    isOpen: false,
    isLoadingSubmit: false,
    onOpen: () => {},
    handleChangeDisable: () => {},
    handleChangeLoading: () => {},
    rerender: 0,
    repeatRequest: () => {},
    modalData: '',
    modalState: initialState,
    changeModalData: () => {},
    onClose: () => {},
    changeModalState: () => {}
});

const cx = classNames.bind(styles);

const ModalContextProvider = () => {
    const [modalState, setModalState] = useState<TModal>(initialState);
    const [isOpen, setIsOpen] = useState(false);
    const [modalData, setModalData] = useState<any>('');
    const [rerender, setRerender] = useState(0);
    const [isDisableSubmit, setIsDisableSubmit] = useState(false);
    const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
    const { submitLocale } = useLocales();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (isOpen) {
            const launchers = document.querySelectorAll('[style="visibility: visible;"]');

            if (launchers.length > 0) {
                launchers.forEach((launcher) => {
                    (launcher as HTMLElement).style.visibility = 'hidden';
                });
            }
        }
        return () => {
            const oldScript = document.getElementById('ze-snippet');
            const launchers = document.querySelectorAll('[style="visibility: hidden;"]');
            if (oldScript) {
                document.body.removeChild(oldScript);
            }
            if (launchers.length > 0) {
                launchers.forEach((launcher) => {
                    (launcher as HTMLElement).style.visibility = 'visible';
                });
            }
        };
    }, [isOpen]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : 'auto';
    }, [isOpen]);

    const onOpen = (value: TOpenModal) => {
        const modal = modals.find((modal) => modal.modalId === value.modalId);

        if (modal) {
            setModalState({
                ...modal,
                ...value
            });
            setIsOpen(true);
        }
    };

    const handleCloseModal = () => {
        requestAnimationFrame(() => {
            setModalState(initialState);
        });
        if (modalState.onClose) {
            modalState.onClose();
        }
        if (location.search) {
            navigate(location.pathname);
        }
        setIsOpen(false);
        setModalData(undefined);
        setIsDisableSubmit(false);
        setIsLoadingSubmit(false);
    };

    const changeModalData = (value: any) => {
        setModalData(value);
    };

    const handleChangeDisable = (val: boolean) => {
        setIsDisableSubmit(val);
    };

    const handleChangeLoading = (val: boolean) => {
        setIsLoadingSubmit(val);
    };

    return (
        <ModalContext.Provider
            value={{
                isOpen: !!modalState.modalId,
                onOpen,
                rerender,
                isLoadingSubmit,
                modalData,
                onClose: handleCloseModal,
                changeModalData,
                handleChangeDisable,
                handleChangeLoading,
                modalState,
                changeModalState: (val: Partial<TModal>) =>
                    setModalState((state) => ({ ...state, ...val })),
                repeatRequest: () => setRerender((state) => state + 1)
            }}>
            <Outlet />
            <div className={cx([styles.wrapper, { isOpen }])}>
                <div
                    onClick={() => !modalState.ignoreOverlay && handleCloseModal()}
                    className={cx([styles.overlay, { isOpen }])}
                />
                {isOpen && (
                    <motion.div
                        {...(modalState.variant === 'drawer' ? drawerAnimation : modalAnimation)}
                        className={cx([styles.dialog, modalState.variant])}>
                        {modalState.isHeader && (
                            <div className={styles.header}>
                                <button onClick={handleCloseModal}>
                                    <Icon name="close" width={24} height={24} />
                                </button>
                            </div>
                        )}
                        <div className={styles.container}>
                            {modalState.Component && (
                                <modalState.Component
                                    onSubmit={modalState.onSubmit}
                                    onClose={handleCloseModal}
                                    modalData={modalData}
                                    changeModalData={changeModalData}
                                    handleChangeDisable={handleChangeDisable}
                                    handleChangeLoading={handleChangeLoading}
                                    isLoadingSubmit={isLoadingSubmit}
                                    modalState={modalState}
                                    changeModalState={(val: TModal) => setModalState(val)}
                                />
                            )}
                            {modalState.isFooter && (
                                <div className={styles.footer}>
                                    {modalState.cancelLabel && (
                                        <Button
                                            variant="outlined-primary"
                                            size="medium"
                                            onClick={handleCloseModal}>
                                            {submitLocale(modalState.cancelLabel)}
                                        </Button>
                                    )}
                                    {modalState.confirmLabel && (
                                        <Button
                                            variant={modalState.submitVariant || 'primary'}
                                            size="medium"
                                            disabled={isDisableSubmit}
                                            isLoading={isLoadingSubmit}
                                            onClick={() => {
                                                if (modalState.onSubmit) {
                                                    modalState.onSubmit(modalData);
                                                }
                                            }}>
                                            {submitLocale(modalState.confirmLabel)}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>
        </ModalContext.Provider>
    );
};

export default ModalContextProvider;
